<template>
  <aw-job-list v-if="showCareerJobListPageFromStore" />
  <aw-page v-else />
</template>

<script setup lang="ts">
  import { computed, defineAsyncComponent } from 'vue';
  import { navigateTo, useNuxtApp } from 'nuxt/app';
  import { extractCatalogSlug, extractResourceFromPath, resetPath, RESOURCE_TYPE_REDIRECT } from '~~/common/utils/urlResource';
  import { useAwRequestTracing } from '~~/common/composables/useAwRequestTracing';
  import { URL_PREFIX } from '~~/common/config/UrlConfig';
  import { useUrlResourceStore } from '~~/common/stores/urlResource';
  import { useOrderStore } from '~~/common/stores/order';
  import { useCareerStore } from '~~/common/stores/career';
  import { slugifyPremise } from '~~/common/composables/useUrls';
  // Namespace import so `definePageMeta` extraction keeps working: as a named import the plain string
  // constant gets inlined and renamed by the bundler, and the renamed declaration does not travel into
  // the extracted chunk — leaving `F_LOCATION is not defined` at runtime.
  import * as CareerFilter from '~~/common/config/CareerFilter';
  import {
    MSG_CATALOG_NOT_FOUND,
    setCookieMsg,
  } from '~~/common/server/globalMiddlewareConstants';
  import { staticRedirectPages } from '~~/shop/app/config/RedirectConfig';

  const AwPage = defineAsyncComponent(() => import('~~/common/components/Page/AwPage.vue'));
  const AwJobList = defineAsyncComponent(() => import('~~/shop/app/components/Page/Career/JobList/AwJobList.vue'));
  const {
    showCareerJobListPage,
  } = useCareerStore();

  defineOptions({
    name: 'FallbackPageShop',
  });
  const showCareerJobListPageFromStore = computed(() => {
    return showCareerJobListPage;
  });
  const {
    $logger,
  } = useNuxtApp();


  // eslint-disable-next-line no-undef
  definePageMeta({
    layout: 'dynamic-content',
    validate: async (route) => {
      const nuxtApp = useNuxtApp();
      const {
        fullPath,
        params,
      } = route;

      const {
        $localePath,
        $logger: validateLogger,
      } = nuxtApp;

      const urlResourceStore = useUrlResourceStore();
      const requestId = useAwRequestTracing().getFeServerRequestId();
      const {
        fetchCareerAdvertisementFilters,
        buildCareerFiltersSelectForm,
        setShowCareerJobListPage,
      } = useCareerStore();

      const splitByQuestionMarkPath = fullPath.split('?');
      const questionMarkLessPath = splitByQuestionMarkPath[0] ?? '';
      const splitByAnchorPath = questionMarkLessPath.split('#');
      const path = splitByAnchorPath[0] ?? '';

      const matchedPage = Object.values(staticRedirectPages).find(pageConfig => pageConfig.regex.test(path));
      if (matchedPage) {
        // We need to return true here, because we want to run the redirectMiddleware.global.js middleware to handle the redirect.
        // If we didn't return true here, the middleware won't run because the url is not valid, and we got an 404, the page doesn't exist error.
        return true;
      }

      if (fullPath.includes('/karrier')) {
        const possibleFilters = buildCareerFiltersSelectForm(await fetchCareerAdvertisementFilters());
        const slug = params.slug;
        const locationValues = possibleFilters?.[CareerFilter.F_LOCATION]?.values || {};
        const loc = (slug && slug.length > 1
          ? Object.values(locationValues).find((v) => {
            return slugifyPremise({ premise: v.name }) === slug[1];
          })
          : null
        )?.id;

        if (fullPath === '/karrier' || fullPath.includes('/karrier?filters') || fullPath === '/en/karrier' || fullPath.includes('/en/karrier?filters') || loc) {
          setShowCareerJobListPage(true);
          return true;
        } else {
          setShowCareerJobListPage(false);
        }
      } else {
        setShowCareerJobListPage(false);
      }

      const resource = extractResourceFromPath(fullPath);
      if (resource) {
        urlResourceStore.setData({
          type: 'simple',
          resourceType: resource.resourceType,
          resourceId: resource.resourceId,
          slug: fullPath,
        });
        return true;
      } else { // Other URL resource. eg.:
        const anchorlessFullPath = fullPath.split('#')[0];
        const resetedFullPath = resetPath(anchorlessFullPath, URL_PREFIX);
        const catalogSlug = extractCatalogSlug(resetedFullPath);
        await urlResourceStore.fetchByUrl(catalogSlug || resetedFullPath || '404');
        if (urlResourceStore.data?.resourceType === 'catalog' && urlResourceStore.data.resourceId) {
          try {
            const { resourceId } = urlResourceStore.data;
            const flipbookUrl = await urlResourceStore.fetchFlipbookUrl(resourceId);
            if (typeof flipbookUrl === 'string' && flipbookUrl.length > 0) {
              if (/^\/?(catalog|katalogusok)/.test(flipbookUrl) || flipbookUrl === resetedFullPath) {
                throw new Error(`flipbookUrl recursion for catalog ${String(resourceId)}`);
              }
              navigateTo(flipbookUrl, {
                external: true,
                redirectCode: 302,
              });
              return true;
            }
          } catch (error) {
            validateLogger.error(error);
          }
          return handleCatalogNotFound();
        }
        if (catalogSlug) {
          return handleCatalogNotFound();
        }

        const redirectData = urlResourceStore.redirectData;
        if (redirectData) {
          const { fromUrl, slug, type } = redirectData;

          if (type === RESOURCE_TYPE_REDIRECT && fromUrl !== slug) {
            return true;
          }
        }
        return Boolean(urlResourceStore.data?.resourceId);
      }

      function handleCatalogNotFound () {
        setCookieMsg({
          nuxtApp,
          cb (redirectMsg) {
            redirectMsg[MSG_CATALOG_NOT_FOUND] = {
              requestId,
            };
          },
        });
        navigateTo($localePath('catalog'), {
          external: true,
          redirectCode: 302,
        });
        return true;
      }
    },
  });
  try {
    const orderStore = useOrderStore();
    // reset paymentData on category list page or product page or search
    if (orderStore.paymentData) {
      orderStore.setPaymentData({
        paymentData: null,
        checkoutType: undefined,
      });
    }
    // reset completedOrders data on category list page or product page or search
    if (orderStore.completedOrders) {
      orderStore.resetCompletedOrders();
    }
  } catch (err) {
    $logger.error(err);
  }
</script>
